/* October 2026 First Look landing page.
   Two jobs: submit every signup form to Klaviyo, and run the claim modal.
   This page runs during live show pushes, so nothing here is allowed to fail
   silently: every failure path logs to the console AND shows the visitor a line. */
(function () {
  'use strict';

  /* ------------------------------------------------------------------
     Klaviyo signup forms
     ------------------------------------------------------------------ */

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function showMessage(form, text, kind) {
    var msg = form.querySelector('[data-oct-msg]');
    if (!msg) return;
    msg.textContent = text;
    msg.className = 'oct-form__msg oct-form__msg--' + kind;
    msg.hidden = false;
  }

  function clearMessage(form) {
    var msg = form.querySelector('[data-oct-msg]');
    if (!msg) return;
    msg.hidden = true;
    msg.textContent = '';
    msg.className = 'oct-form__msg';
  }

  function trackLead() {
    // The Meta pixel is delivered by Shopify's web pixel manager and loads async,
    // so it may legitimately not be there yet. Never let it break the success state.
    try {
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead');
      }
    } catch (err) {
      console.error('[oct-landing] fbq Lead failed', err);
    }
  }

  function submitForm(form) {
    var emailField = form.querySelector('[name="email"]');
    var nameField = form.querySelector('[name="first_name"]');
    var phoneField = form.querySelector('[name="phone"]');
    var phoneDigits = (phoneField && phoneField.value ? phoneField.value : '').replace(/\D/g, '');
    if (phoneDigits.length === 11 && phoneDigits.charAt(0) === '1') phoneDigits = phoneDigits.slice(1);
    var phoneE164 = phoneDigits.length === 10 ? '+1' + phoneDigits : '';
    var button = form.querySelector('button[type="submit"]');

    var email = (emailField && emailField.value ? emailField.value : '').trim();
    var firstName = (nameField && nameField.value ? nameField.value : '').trim();

    var errorText = form.getAttribute('data-error') || 'That email did not go through. Check the address and try again.';

    if (!firstName) {
      if (nameField) {
        nameField.setAttribute('aria-invalid', 'true');
        nameField.focus();
      }
      showMessage(form, 'Please add your first name so I know who I am writing to.', 'error');
      return;
    }
    if (nameField) nameField.removeAttribute('aria-invalid');

    if (!EMAIL_RE.test(email)) {
      if (emailField) {
        emailField.setAttribute('aria-invalid', 'true');
        emailField.focus();
      }
      showMessage(form, errorText, 'error');
      return;
    }
    if (emailField) emailField.removeAttribute('aria-invalid');

    clearMessage(form);

    var companyId = form.getAttribute('data-company');
    var listId = form.getAttribute('data-list');
    var revision = form.getAttribute('data-revision');
    var source = form.getAttribute('data-source');

    if (!companyId || !listId) {
      console.error('[oct-landing] form is missing data-company or data-list', form);
      showMessage(form, errorText, 'error');
      return;
    }

    var properties = {};
    if (source) properties['Acquisition Source'] = source;

    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                first_name: firstName,
                phone_number: phoneE164 || undefined,
                properties: properties,
                subscriptions: (function () {
                  var subs = { email: { marketing: { consent: 'SUBSCRIBED' } } };
                  // Optional mobile: only when a valid US number was typed. Consent text sits under the field.
                  if (phoneE164) subs.sms = { marketing: { consent: 'SUBSCRIBED' } };
                  return subs;
                })()
              }
            }
          }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    };

    var originalLabel = button ? button.textContent : '';
    if (button) {
      button.disabled = true;
      button.textContent = 'Adding you';
    }

    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: revision || '2025-07-15'
      },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        if (res.ok) return null;
        return res.text().then(function (body) {
          throw new Error('Klaviyo responded ' + res.status + ': ' + body);
        });
      })
      .then(function () {
        form.classList.add('is-done');
        showMessage(form, form.getAttribute('data-success') || "You're in.", 'success');
        var msg = form.querySelector('[data-oct-msg]');
        if (msg) {
          msg.setAttribute('tabindex', '-1');
          msg.focus();
        }
        trackLead();
      })
      .catch(function (err) {
        console.error('[oct-landing] Klaviyo subscribe failed', err);
        showMessage(form, errorText, 'error');
        if (button) {
          button.disabled = false;
          button.textContent = originalLabel;
        }
      });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target && event.target.closest ? event.target.closest('[data-oct-form]') : null;
    if (!form) return;
    event.preventDefault();
    if (form.classList.contains('is-done')) return;
    submitForm(form);
  });

  /* ------------------------------------------------------------------
     Smooth scroll for the in-page CTAs

     Done here rather than with CSS `scroll-behavior: smooth` on <html>, because
     that breaks every scroll-and-stitch full-page screenshot tool: the scroll
     position lags the tile grab, so captures clip the top and repeat content.
     On click only, so programmatic scrolling stays instant.
     ------------------------------------------------------------------ */

  document.addEventListener('click', function (event) {
    var link = event.target && event.target.closest ? event.target.closest('a[href^="#"]') : null;
    if (!link) return;
    var id = link.getAttribute('href');
    if (!id || id === '#') return;

    var target = null;
    try {
      target = document.querySelector(id);
    } catch (err) {
      console.error('[oct-landing] bad in-page link target', id, err);
      return;
    }
    if (!target) {
      console.error('[oct-landing] in-page link points at nothing', id);
      return;
    }

    event.preventDefault();
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });

    // Move focus so keyboard users land where the page just scrolled.
    var field = target.querySelector('input, button, [tabindex]');
    if (field && typeof field.focus === 'function') {
      field.focus({ preventScroll: true });
    }
  });

  /* ------------------------------------------------------------------
     Click-to-play video
     ------------------------------------------------------------------ */

  document.addEventListener('click', function (event) {
    var cover = event.target && event.target.closest ? event.target.closest('[data-oct-video-cover]') : null;
    if (!cover) return;
    var shell = cover.closest('[data-oct-video]');
    var video = shell ? shell.querySelector('video') : null;
    if (!video) {
      console.error('[oct-landing] video cover with no video element');
      return;
    }
    cover.hidden = true;
    video.setAttribute('controls', 'controls');
    // Set here rather than through video_tag, which drops arguments it does not know.
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    var playing = video.play();
    if (playing && typeof playing.catch === 'function') {
      playing.catch(function (err) {
        // Autoplay policies can still refuse. Give the visitor the native controls back.
        console.error('[oct-landing] video play refused', err);
        cover.hidden = false;
      });
    }
  });

  /* ------------------------------------------------------------------
     Claim modal
     ------------------------------------------------------------------ */

  var FOCUSABLE = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(', ');
  var openModal = null;
  var lastTrigger = null;

  function focusables(modal) {
    return Array.prototype.filter.call(modal.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function open(modal, trigger) {
    if (!modal) return;
    modal.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    openModal = modal;
    lastTrigger = trigger || null;
    var items = focusables(modal);
    if (items.length) items[0].focus();
  }

  function close() {
    if (!openModal) return;
    openModal.hidden = true;
    document.documentElement.style.overflow = '';
    if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
    openModal = null;
    lastTrigger = null;
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    if (target.closest('[data-oct-close]')) {
      event.preventDefault();
      close();
      return;
    }

    var trigger = target.closest('[data-oct-open]');
    if (trigger) {
      event.preventDefault();
      var id = trigger.getAttribute('data-oct-open');
      var modal = document.getElementById(id);
      if (!modal) {
        console.error('[oct-landing] no modal found for id', id);
        return;
      }
      open(modal, trigger);
      return;
    }

    // Tapping anywhere on an available card opens its modal, but the chip button
    // stays the real accessible control.
    var card = target.closest('[data-oct-card]');
    if (card) {
      var chip = card.querySelector('[data-oct-open]');
      if (chip) chip.click();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (!openModal) return;

    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    var items = focusables(openModal);
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    } else if (!openModal.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    }
  });
})();
